import DescriptionList from 'ant-design-pro/lib/DescriptionList';
import PageHeader from 'ant-design-pro/lib/PageHeader';
import {
  Alert,
  Avatar,
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Table,
} from 'antd';
import { RangePickerValue } from 'antd/lib/date-picker/interface';
import FormItem from 'antd/lib/form/FormItem';
import { RadioChangeEvent } from 'antd/lib/radio';
import { SelectValue } from 'antd/lib/select';
import { ColumnFilterItem } from 'antd/lib/table';
import gql from 'graphql-tag';
import * as moment from 'moment';
import queryString from 'query-string';
import React, { Component } from 'react';
import { Mutation, Query } from 'react-apollo';
import * as CopyToClipboard from 'react-copy-to-clipboard';
import { Link, RouteComponentProps } from 'react-router-dom';
import { mobileMasking, nameMasking } from 'src/utils/masking';
import * as xlsx from 'xlsx';

import { Panel } from '../../components/Panel';
import { FormatDate } from '../../components/table/Date';
import { config } from '../../config';
import { cancelClockInMutation, doClockInMutation } from '../../mutations/clockIn';
import { RETURN_VISIT_MUTATION } from '../../mutations/returnVisit';
import { client } from '../../services/api';
import { DateRange } from '../../types/base';
import { Enrollment, EnrollmentGroup } from '../../types/enrollment';
import { Child } from '../../types/family';
import {
  ActivityRewardStatus,
  GClockInActivityJoinedRecordWithStatus,
  GSLMActivityJoinedRecordWithStatus,
  MarketActivityType,
  MarketingActivity,
  RuleUsersLimitSchema,
  UserActivityJoinedRecordWithStatus,
} from '../../types/marketingActivity';
import { SLMRelationship, SLMRelationshipStatus } from '../../types/slm';
import { AccountType, UserAccount } from '../../types/userAccount';
import { LessonProgresses, LessonStatus, MiniGameResult } from '../enrollment/Enrollment';
import { USER_CONTACT_RECORD_FEEDBACK_NAME } from '../user/constants';
import {
  ChildAbility,
  USER_CONTACT_RECORD_INTENT_NAME,
  UserContactRecord,
  UserContactRecordFeedback,
  UserContactRecordIntent,
  UserContactRecordType,
} from '../user/Users';
import { AddRecord } from './AddRecord';
import { getAfterLearnKnowledgeLevel, getChildAbilityFromKnowledgeLevel } from './CalcKnowledgeLevel';
import { MainTable } from './GroupDetailMainTable';
import { SelectDisplayColumn } from './SelectDisplayColumn';

const { Description } = DescriptionList;

const groupListQuery = gql`
  query getGroupListQuery($skuIds: [ID], $teacherIds: [ID], $name: String) {
    enrollmentGroups(skuIds: $skuIds, teacherIds: $teacherIds, name: $name) {
      id
      name
    }
  }
`;

export const marketingActivityQuery = gql`
  query getMarketingActivityQuery {
    marketingActivities {
      id
      name
      desc
      weight
      type
      rules {
        ruleKey
        ruleValue
      }
    }
  }
`;

export const groupDetailQuery = gql`
  query getGroupDetailQuery($groupId: ID!, $dateRange: DateRange!, $activityId: ID!, $miniGameName: String!) {
    enrollmentGroups(ids: [$groupId]) {
      id
      name
      createdAt
      sku {
        id
        name
        start
        end
        quizEndAt
      }
      teacher {
        id
        name
        avatarUrl
        wechatQrCode
      }
      enrollments {
        id
        user {
          id
          name
          mobile
          region
          family {
            id
            region
            children {
              id
              name
              gender
              birthday
              createdAt
              personas {
                id
                type
                value
                createdAt
              }
            }
          }
          accounts {
            id
            balance
            accountType
          }
          clockInRecords {
            id
            clockedInAt
            status
          }
          clockInActivityJoinedRecords(activityId: $activityId) {
            sourceId
            quantity
            status
            scheduledTime
            clockedInAt
          }
          checkInActivityJoinedRecords {
            quantity
            sourceId
            status
            userId
          }
          slmActivityJoinedRecords(activityId: $activityId) {
            sourceId
            quantity
            status
            scheduledTime
            fundedAt
          }
          slmRelationships {
            id
            status
            isBindMobile
          }
          contactRecords {
            id
            feedback
            isSuccess
            type
            intent
            remark
            createdAt
          }
        }
        returnVisit {
          newLearnedLessons {
            id
            serialId
            displayId
            name
          }
          lastVisitedLessons {
            id
            serialId
            displayId
            name
          }
          lastReturnVisitAt
        }

        lessonProgresses {
          serialId
          status
        }
        miniGameResults(miniGameName: $miniGameName) {
          createdAt
          id
          result
          data {
            knowledgeLevel
            level
          }
        }
        createdAt
        rankingInGroup(dateRange: $dateRange) {
          userId
          rank
          totalScore
        }
      }
    }
  }
`;

export enum ClockInRewardStatus {
  SCORE_SENT, // 已发放积分
  NONE_CLOCKED_IN, // 未打卡
  CLOCKED_IN, // 待发放积分
}

export const ClockInRewardStatusText = {
  [ClockInRewardStatus.SCORE_SENT]: '已发放积分',
  [ClockInRewardStatus.NONE_CLOCKED_IN]: '未打卡',
  [ClockInRewardStatus.CLOCKED_IN]: '待发放积分',
};

export enum ClockInStatus {
  CLOCKED_IN,
  REVOKED,
}

enum ClockInFilter {
  ALL,
  POINT_SENT, // 已发放积分
  NONE_CLOCKED_IN, // 未打卡
  CLOCKED_IN, // 待发放积分
}

export enum ColumnDisplayList {
  SUPERINTENDENT, // 回访督学
  FOLLOW_UP_ACTIVITIES, // 活动跟进
  ADD_WECHAT, // 添加微信
  EVALUATION_LEVEL, // 测评等级
  INVITATION_FOR_RETURN_FEE, // 请返学费
  TOTAL_LESSON_PROGRESS, // 累计完课
  TOTAL_CLOCK_IN, // 累计打卡
  LESSON_GIVING_ACTIVITIES, // 赠课活动
  RENEWAL_STATUS, // 续费状态
  INTENTIONALITY, // 意向度
  LAST_INTENT, // 跟进记录
  NUMBER_OF_CALLS, // 通话次数
}

export interface MarketingActivityData {
  marketingActivities: MarketingActivity[];
}

interface GroupDetailData {
  enrollmentGroups: EnrollmentGroup[];
}

interface ClockInActivityJoinedRecord {
  sourceId: number;
  quantity: number;
  status: number;
  scheduledTime: Date;
  clockedInAt: Date;
}

interface GroupDetailStates {
  rankingsDateRange: DateRange;
  isInputGroupNameModalDisplay: boolean;
  inputGroupName: string;
  isInputGroupNameMessageAlert: boolean;
  clockInDateRange: DateRange;
  selectedClockInFilter: ClockInFilter;
  marketingActivityFilterOptions: MarketingActivity[];
  selectedMarketingActivityFilter: number;
  marketingActivities: MarketingActivity[];
  mobile?: string;
  nickName?: string;
  isAddWeChatRecord: boolean;
  isAddRecordModalDisplay: boolean;
  columnDisplayList: {
    isSuperintendentDisplay: boolean;
    isFollowUpActivitiesDisplay: boolean;
    isAddWechatDisplay: boolean;
    isEvallutionLevelDisplay: boolean;
    isInvitationForReturnFeeDisplay: boolean;
    isTotalLessonProgressDisplay: boolean;
    isTotalClockInDisplay: boolean;
    isLessonGivingActivitiesDisplay: boolean;
    isRenewalStatusDisplay: boolean;
    isIntentionalityDisplay: boolean;
    isLastIntentDisplay: boolean;
    isNumberOfCallsDisplay: boolean;
  };
  currentUserId?: number;
  intent?: UserContactRecordIntent;
  remark?: string;
  feedback?: UserContactRecordFeedback;
  isCallSuccess?: boolean;
  currentGroupId?: number;
  searchText: React.Key;
}
const updateGroupNameMutation = gql`
  mutation updateEnrollmentGroup($id: ID, $name: String) {
    updateEnrollmentGroup(id: $id, name: $name) {
      id
      name
    }
  }
`;

const doBatchClockInMutation = gql`
  mutation doBatchClockIn($activityId: Int, $date: Date, $userIds: [Int]) {
    doBatchClockIn(activityId: $activityId, date: $date, userIds: $userIds) {
      id
      clockedInAt
      status
    }
  }
`;

const cancelBatchClockInMutation = gql`
  mutation cancelBatchClockIn($ids: [Int]) {
    cancelBatchClockIn(ids: $ids) {
      id
      clockedInAt
      status
    }
  }
`;

type GroupDetailProps = RouteComponentProps<{ id: string; skuId: string }>;

export class GroupDetail extends Component<GroupDetailProps, GroupDetailStates> {
  private readonly clockInStatusFilterOptions = [
    ClockInFilter.ALL,
    ClockInFilter.POINT_SENT,
    ClockInFilter.NONE_CLOCKED_IN,
    ClockInFilter.CLOCKED_IN,
  ];
  private readonly clockInStatusFilterOptionText = {
    [ClockInFilter.ALL]: '全部',
    [ClockInFilter.POINT_SENT]: '已发放积分',
    [ClockInFilter.NONE_CLOCKED_IN]: '未打卡',
    [ClockInFilter.CLOCKED_IN]: '已打卡待发放',
  };
  private readonly skuId = Number(this.props.match.params.skuId);
  private readonly teacherId = Number(queryString.parse(this.props.location.search).teacherId);
  constructor(props: GroupDetailProps) {
    super(props);
    this.state = {
      rankingsDateRange: {
        from: moment().startOf('day').toDate(),
        to: moment().endOf('day').toDate(),
      },
      clockInDateRange: {
        from: moment().startOf('day').toDate(),
        to: moment().endOf('day').toDate(),
      },
      searchText: '',
      marketingActivityFilterOptions: [],
      selectedMarketingActivityFilter: 0,
      isInputGroupNameModalDisplay: false,
      inputGroupName: '',
      isInputGroupNameMessageAlert: false,
      selectedClockInFilter: ClockInFilter.ALL,
      marketingActivities: [],
      isAddWeChatRecord: false,
      isAddRecordModalDisplay: false,
      columnDisplayList: {
        isSuperintendentDisplay: true,
        isFollowUpActivitiesDisplay: true,
        isAddWechatDisplay: true,
        isEvallutionLevelDisplay: true,
        isInvitationForReturnFeeDisplay: true,
        isTotalLessonProgressDisplay: true,
        isTotalClockInDisplay: true,
        isLessonGivingActivitiesDisplay: true,
        isRenewalStatusDisplay: true,
        isIntentionalityDisplay: true,
        isLastIntentDisplay: true,
        isNumberOfCallsDisplay: true,
      },
    };
  }

  async componentWillMount() {
    const { id } = this.props.match.params;
    const response = await client.query<MarketingActivityData>({
      query: marketingActivityQuery,
    });
    const marketingActivities = (response.data && response.data.marketingActivities) || [];
    const marketingActivitiesBySkuId = this.filterMarketingActivitiesBySku(marketingActivities);

    this.setState({
      marketingActivities,
      marketingActivityFilterOptions: marketingActivitiesBySkuId,
      currentGroupId: Number(id),
    });
  }

  filterMarketingActivitiesBySku = (marketingActivities: MarketingActivity[]) => {
    const marketingActivitiesBySkuId = marketingActivities.filter(marketingActivity => {
      const rules = marketingActivity.rules;
      return rules.some(rule => {
        if (rule.ruleKey === 'MA_RULE_USERS_LIMIT_CONFIG') {
          const ruleValue = rule.ruleValue as RuleUsersLimitSchema;
          return ruleValue.ownedSkuId === Number(this.skuId);
        }
        return false;
      });
    });
    const marketingActivitiesBySkuIdList = marketingActivitiesBySkuId.filter(
      marketingActivity =>
        marketingActivity.type === MarketActivityType.CLOCK_IN || marketingActivity.type === MarketActivityType.POINT,
    );
    return marketingActivitiesBySkuIdList;
  };
  showModal = () => {
    this.setState({
      isInputGroupNameModalDisplay: true,
    });
  };
  onPointStatusFilterChange(value: SelectValue) {
    this.setState({
      selectedClockInFilter: Number(value) as ClockInFilter,
    });
  }
  exportExcel = (enrollmentGroup: EnrollmentGroup | null) => {
    if (!enrollmentGroup) {
      return;
    }
    enum ChildPersonaType {
      GRADE,
      MATH_ABILITY,
      ENGLISH_ABILITY,
      CHINESE_ABILITY,
    }
    const CHILD_ABILITY_DESCRIPTIONS = {
      // Math
      [ChildAbility.MATH_NO_BASIC]: '零基础',
      [ChildAbility.MATH_KNOW_NUMBER_NO_ADDTION_OR_SUBTRACTION]: '只认识数字，但不会加减',
      [ChildAbility.MATH_ADDITION_WITHIN_5_NO_SUBTRACTION]: '5以内加法会，减法不会',
      [ChildAbility.MATH_ADDITION_AND_SUBTRACTION_WITHIN_5_VERY_SKILLED]: '5以内加减法都很熟练',
      [ChildAbility.MATH_ADDITION_WITHIN_10_NO_SUBTRACTION]: '10以内加法会，减法不会',
      [ChildAbility.MATH_ADDITION_AND_SUBTRACTION_WITHIN_10_NEED_HAND_INDEX]: '10以内加减法会，但需掰手指',
      [ChildAbility.MATH_ADDITION_AND_SUBTRACTION_WITHIN_10_VERY_SKILLED]: '10以内加减法都很熟练',
      [ChildAbility.MATH_ADDITION_WITHIN_20_NO_SUBTRACTION]: '20以内加法会，减法不会',
      [ChildAbility.MATH_ADDITION_AND_SUBTRACTION_WITHIN_20_VERY_SKILLED]: '20以内加减法都会',
      [ChildAbility.MATH_ADDITION_AND_SUBTRACTION_WITHIN_100_VERY_SKILLED]: '100以内加减法都会',
      [ChildAbility.OTHER]: '其他',
    };
    enum Grade {
      // 幼儿园 小班
      KINDERGARTEN_GRADE_1 = 2000,
      // 幼儿园 中班
      KINDERGARTEN_GRADE_2 = 2010,
      // 幼儿园 大班
      KINDERGARTEN_GRADE_3 = 2020,
      // 小学 一年级
      PRIMARY_SCHOOL_GRADE_1 = 4000,
      //  其他
      OTHER = 101000,
    }

    enum Gender {
      UNKNOWN,
      MALE,
      FEMALE,
    }

    const GENDER_STRING = {
      [Gender.MALE]: '小王子',
      [Gender.FEMALE]: '小公主',
    };

    const CHILD_GRADE_DESCRIPTIONS = {
      [Grade.KINDERGARTEN_GRADE_1]: '幼儿园小班',
      [Grade.KINDERGARTEN_GRADE_2]: '幼儿园中班',
      [Grade.KINDERGARTEN_GRADE_3]: '幼儿园大班',
      [Grade.PRIMARY_SCHOOL_GRADE_1]: '一年级',
      [Grade.OTHER]: '其他',
    };

    const userInfos: {}[] = [];

    for (const data of enrollmentGroup.enrollments) {
      const user = data.user;
      if (!user || !user.mobile) {
        continue;
      }

      const maskedMobile = mobileMasking(user.mobile);
      let afterLearnknowledgeLevel = '';
      if (user.family && user.family.children) {
        afterLearnknowledgeLevel = getAfterLearnKnowledgeLevel(user.family.children, enrollmentGroup.sku.quizEndAt);
      }

      const miniGameResults = data.miniGameResults.sort((a, b) => a.id - b.id);
      const [firstQuiz] = miniGameResults;
      const lastQuiz = this.getBeforeQuiz(miniGameResults, enrollmentGroup.sku.quizEndAt);

      const userChildInfo = {
        name: user.name,
        childName: '',
        gender: '',
        birthday: '',
        mobile: maskedMobile,
        region: user.family && user.family.region,
        mathAbility: '',
        grade: '',
        accountCumulativeCompleteCourse: this.getAccountCumulativeCompleteCourse(data.lessonProgresses),
        SLMTotalInvitedCount: this.getSLMTotalInvitedCount(user.slmRelationships),
        SLMTotalBoughtCount: this.getSLMTotalBoughtCount(user.slmRelationships),
        SLMPendingPoint: this.getSLMPendingPoint(user.slmActivityJoinedRecords),
        SLMSentPoint: this.getSLMSentPoint(user.slmActivityJoinedRecords),
        accountBalancePoint: this.getAccountBalancePoint(user.accounts),
        firstQuizLevel: firstQuiz && firstQuiz.data.level,
        firstQuizLevelDisplay: firstQuiz && getChildAbilityFromKnowledgeLevel(firstQuiz.data.knowledgeLevel),
        lastQuizLevel: lastQuiz && lastQuiz.data.level,
        lastQuizLevelDisplay: lastQuiz && getChildAbilityFromKnowledgeLevel(lastQuiz.data.knowledgeLevel),
        afterLevel: afterLearnknowledgeLevel,
      };

      if (user.family && user.family.children) {
        const [child] = user.family.children.sort((a, b) => b.id - a.id);
        if (child) {
          userChildInfo.gender = GENDER_STRING[child.gender];
          for (const personas of child.personas) {
            if (personas.type === ChildPersonaType.MATH_ABILITY) {
              userChildInfo.mathAbility = CHILD_ABILITY_DESCRIPTIONS[personas.value];
            }
            if (personas.type === ChildPersonaType.GRADE) {
              userChildInfo.grade = CHILD_GRADE_DESCRIPTIONS[personas.value];
            }
          }

          userChildInfo.birthday = child.birthday && moment(child.birthday).format('L');
          userChildInfo.childName = nameMasking(child.name);
        }
      }

      userInfos.push(userChildInfo);
    }
    const workbook = xlsx.utils.book_new();
    const sheet1 = xlsx.utils.json_to_sheet(userInfos);
    xlsx.utils.book_append_sheet(workbook, sheet1);
    xlsx.writeFile(workbook, 'out.xlsx');
  };

  handleCancel = () => {
    this.setState({
      inputGroupName: '',
      isInputGroupNameMessageAlert: false,
      isInputGroupNameModalDisplay: false,
    });
  };
  onNameChange = (e: { target: { value: string } }) => {
    this.setState({ inputGroupName: e.target.value });
  };
  clockInStatusText(activityJoinedRecords: GClockInActivityJoinedRecordWithStatus[]) {
    const lastDate = moment(this.state.clockInDateRange.to);
    const activityJoinedRecord = activityJoinedRecords.find(
      activityJoinedRecord =>
        !!activityJoinedRecord.clockedInAt && moment(activityJoinedRecord.clockedInAt).isSame(lastDate, 'day'),
    );

    if (!activityJoinedRecord) {
      return this.clockInStatusFilterOptionText[ClockInFilter.NONE_CLOCKED_IN];
    }

    if (activityJoinedRecord.status === ActivityRewardStatus.SENT) {
      return this.clockInStatusFilterOptionText[ClockInFilter.POINT_SENT];
    }

    if (activityJoinedRecord.status === ActivityRewardStatus.SCHEDULED) {
      return this.clockInStatusFilterOptionText[ClockInFilter.CLOCKED_IN];
    }

    return null;
  }

  checkIsLastDayClockedIn(activityJoinedRecords: GClockInActivityJoinedRecordWithStatus[]) {
    const lastDate = moment(this.state.clockInDateRange.to);
    const activityJoinedRecord = activityJoinedRecords.find(
      activityJoinedRecord =>
        !!activityJoinedRecord.clockedInAt && moment(activityJoinedRecord.clockedInAt).isSame(lastDate, 'day'),
    );

    if (!activityJoinedRecord) {
      return false;
    }

    if (
      activityJoinedRecord.status === ActivityRewardStatus.SENT ||
      activityJoinedRecord.status === ActivityRewardStatus.SCHEDULED
    ) {
      return true;
    }
    return false;
  }

  getBatchClockInButton(enrollments: Enrollment[]) {
    const to = moment(this.state.clockInDateRange.to);
    const now = moment();
    const isToday = now.isSame(to, 'day');

    const clockInAt = moment(this.state.clockInDateRange.to).hour(17).minute(0).second(0).millisecond(0);

    const {
      clockInDateRange,
      marketingActivityFilterOptions,
      selectedMarketingActivityFilter,
      currentGroupId,
    } = this.state;

    const activityId = marketingActivityFilterOptions[selectedMarketingActivityFilter];

    switch (this.state.selectedClockInFilter) {
      case ClockInFilter.POINT_SENT:
      case ClockInFilter.CLOCKED_IN: {
        const ids: number[] = [];

        for (const enrollment of enrollments) {
          const { clockInActivityJoinedRecords } = enrollment.user;
          for (const clockInActivityJoinedRecord of clockInActivityJoinedRecords) {
            if (
              clockInActivityJoinedRecord.status === ActivityRewardStatus.SCHEDULED ||
              clockInActivityJoinedRecord.status === ActivityRewardStatus.SENT
            ) {
              ids.push(clockInActivityJoinedRecord.sourceId);
            }
          }
        }

        return (
          <Mutation
            mutation={cancelBatchClockInMutation}
            variables={{ ids }}
            refetchQueries={[
              {
                query: groupDetailQuery,
                variables: {
                  activityId,
                  dateRange: clockInDateRange,
                  miniGameName: 'admission-quiz',
                  groupId: currentGroupId,
                },
              },
            ]}
          >
            {(cancelBatchClockIn, { loading }) => {
              return (
                <Button
                  loading={loading}
                  type="danger"
                  onClick={async () => {
                    await cancelBatchClockIn();
                  }}
                >
                  批量取消打卡
                </Button>
              );
            }}
          </Mutation>
        );
      }

      case ClockInFilter.NONE_CLOCKED_IN: {
        let buttonText = '批量补打卡';
        if (isToday) {
          buttonText = '批量确认打卡';
        }
        const userIds = enrollments
          .filter(enrollment => !this.checkIsLastDayClockedIn(enrollment.user.clockInActivityJoinedRecords))
          .map(enrollment => enrollment.rankingInGroup.userId);

        return (
          <Mutation
            mutation={doBatchClockInMutation}
            variables={{ activityId, userIds, date: clockInAt }}
            refetchQueries={[
              {
                query: groupDetailQuery,
                variables: {
                  activityId,
                  dateRange: clockInDateRange,
                  miniGameName: 'admission-quiz',
                  groupId: currentGroupId,
                },
              },
            ]}
          >
            {(doBatchClockIn, { loading }) => {
              return (
                <Button
                  loading={loading}
                  type="primary"
                  onClick={async () => {
                    try {
                      await doBatchClockIn();
                      message.success('操作成功！');
                    } catch {
                      message.success('操作失败！');
                    }
                  }}
                >
                  {buttonText}
                </Button>
              );
            }}
          </Mutation>
        );
      }
      default:
    }
    return null;
  }

  getClockInButton(activityJoinedRecords: GClockInActivityJoinedRecordWithStatus[], userId: number) {
    const now = moment().startOf('day');
    const lastDate = moment(this.state.clockInDateRange.to);

    const clockInAt = moment(this.state.clockInDateRange.to).hour(17).minute(0).second(0).millisecond(0);

    const {
      clockInDateRange,
      selectedMarketingActivityFilter,
      marketingActivityFilterOptions,
      currentGroupId,
    } = this.state;

    if (!marketingActivityFilterOptions.length) {
      return (
        <Button type="primary" disabled style={{ height: '50%' }}>
          确认
          <br />
          打卡
        </Button>
      );
    }
    const activityId = marketingActivityFilterOptions[selectedMarketingActivityFilter].id;

    const activityJoinedRecord = activityJoinedRecords.find(
      activityJoinedRecord =>
        !!activityJoinedRecord.clockedInAt && moment(activityJoinedRecord.clockedInAt).isSame(lastDate, 'day'),
    );

    if (!activityJoinedRecord) {
      return (
        <Mutation
          mutation={doClockInMutation}
          variables={{ activityId, userId, date: clockInAt }}
          refetchQueries={[
            {
              query: groupDetailQuery,
              variables: {
                activityId,
                dateRange: clockInDateRange,
                miniGameName: 'admission-quiz',
                groupId: currentGroupId,
              },
            },
          ]}
        >
          {(doClockIn, { loading }) => {
            return (
              <Button
                loading={loading}
                type="primary"
                style={{ height: '50%' }}
                onClick={async () => {
                  try {
                    await doClockIn();
                    message.success('打卡成功！');
                  } catch {
                    message.error('打卡失败！');
                  }
                }}
              >
                <div>{lastDate.isBefore(now) ? '补打卡' : '确认'}</div>
                {!lastDate.isBefore(now) && <div>打卡</div>}
              </Button>
            );
          }}
        </Mutation>
      );
    }

    if (activityJoinedRecord.status === ActivityRewardStatus.SCHEDULED) {
      return (
        <Mutation
          mutation={cancelClockInMutation}
          variables={{ id: activityJoinedRecord.sourceId }}
          refetchQueries={[
            {
              query: groupDetailQuery,
              variables: {
                activityId,
                dateRange: clockInDateRange,
                miniGameName: 'admission-quiz',
                groupId: currentGroupId,
              },
            },
          ]}
        >
          {(cancelClockIn, { loading }) => {
            return (
              <Button
                loading={loading}
                type="danger"
                style={{ height: '50%' }}
                onClick={async () => {
                  await cancelClockIn();
                }}
              >
                取消
                <br />
                打卡
              </Button>
            );
          }}
        </Mutation>
      );
    }

    if (activityJoinedRecord.status === ActivityRewardStatus.SENT) {
      return (
        <Mutation
          mutation={cancelClockInMutation}
          variables={{ id: activityJoinedRecord.sourceId }}
          refetchQueries={[
            {
              query: groupDetailQuery,
              variables: {
                activityId,
                dateRange: clockInDateRange,
                miniGameName: 'admission-quiz',
                groupId: currentGroupId,
              },
            },
          ]}
        >
          {(cancelClockIn, { loading }) => {
            return (
              <Button
                loading={loading}
                type="danger"
                style={{ height: '50%' }}
                onClick={async () => {
                  await cancelClockIn();
                }}
              >
                申请取
                <br />
                消打卡
              </Button>
            );
          }}
        </Mutation>
      );
    }

    return null;
  }

  addRecordButton(isAddWeChatRecord: boolean, userId: number) {
    return (
      <Button
        style={{ height: '50%', padding: 5, marginRight: 10, marginBottom: 5 }}
        type="primary"
        onClick={async () => {
          this.setState({
            isAddWeChatRecord,
            isAddRecordModalDisplay: true,
            currentUserId: userId,
          });
        }}
      >
        <div> {isAddWeChatRecord ? '新增微' : '新增电'}</div>
        <div> {isAddWeChatRecord ? '信跟进' : '话跟进'}</div>
      </Button>
    );
  }

  closeAddRecordModal = () => {
    this.setState({
      isAddRecordModalDisplay: false,
    });
  };

  onIsCallSuccessChange = (e: RadioChangeEvent) => {
    this.setState({
      isCallSuccess: e.target.value,
    });
  };

  onIntentChange = (e: RadioChangeEvent) => {
    this.setState({
      intent: e.target.value,
    });
  };

  onRemarkChange = (e: { target: { value: string } }) => {
    this.setState({ remark: e.target.value });
  };

  onRecordFeedbackChange = (value: UserContactRecordFeedback) => {
    this.setState({ feedback: value });
  };

  submitAddFeedback = () => {
    const { remark, intent, feedback } = this.state;
    if (!intent) {
      message.error('请填写意向度');
      return false;
    }
    if (!feedback) {
      message.error('请填写用户反馈');
      return false;
    }
    if (!remark) {
      message.error('请填写跟进记录');
      return false;
    }
    this.setState({
      isAddRecordModalDisplay: false,
      intent: undefined,
      isCallSuccess: undefined,
      feedback: undefined,
      remark: undefined,
    });
    return true;
  };

  onClockInDateRange(dates: RangePickerValue) {
    const clockInDateRange = {
      from: moment(dates[0]).startOf('day').toDate(),
      to: moment(dates[1]).endOf('day').toDate(),
    };

    this.setState({ clockInDateRange });
  }
  // TODO fix typo
  onClockInFilterChange(value: string, record: any) {
    const currentFilter = Number(value);
    const { clockInActivityJoinedRecords } = record.user;

    if (this.state.selectedClockInFilter !== currentFilter) {
      this.setState({ selectedClockInFilter: currentFilter });
    }

    switch (currentFilter) {
      case ClockInFilter.POINT_SENT:
        return clockInActivityJoinedRecords.find(
          (clockInActivityJoinedRecord: { status: ActivityRewardStatus; clockedInAt: Date }) =>
            clockInActivityJoinedRecord.status === ActivityRewardStatus.SENT &&
            moment(clockInActivityJoinedRecord.clockedInAt).isSame(moment(this.state.clockInDateRange.to), 'day'),
        );
      case ClockInFilter.CLOCKED_IN:
        return clockInActivityJoinedRecords.find(
          (clockInActivityJoinedRecord: { status: ActivityRewardStatus; clockedInAt: Date }) =>
            clockInActivityJoinedRecord.status === ActivityRewardStatus.SCHEDULED &&
            moment(clockInActivityJoinedRecord.clockedInAt).isSame(moment(this.state.clockInDateRange.to), 'day'),
        );
      case ClockInFilter.NONE_CLOCKED_IN:
        return !this.checkIsLastDayClockedIn(clockInActivityJoinedRecords);
      case ClockInFilter.ALL:
      default:
        return record;
    }
  }
  getClockInFilters() {
    const options: ColumnFilterItem[] = [];

    for (const clockInStatusFilterOption of this.clockInStatusFilterOptions) {
      options.push({
        text: this.clockInStatusFilterOptionText[clockInStatusFilterOption],
        value: this.clockInStatusFilterOptions[clockInStatusFilterOption].toString(),
      });
    }
    return options;
  }
  getChildName(children: Child[] = []) {
    // const child = children.find(
    //   child => !!child.personas.find(persona => persona.type === ChildPersonaType.MATH_ABILITY),
    // );
    // return (child && child.name) || '';

    if (children && children.length > 0) {
      const child = children[children.length - 1];
      const maskedChildName = child && nameMasking(child.name);
      return maskedChildName;
    }
    return '';
  }
  getSLMSentPoint(slmActivityJoinedRecords: GSLMActivityJoinedRecordWithStatus[]) {
    let enrnedPoint = 0;
    if (slmActivityJoinedRecords && slmActivityJoinedRecords.length > 0) {
      for (const slmActivityJoinedRecord of slmActivityJoinedRecords) {
        if (slmActivityJoinedRecord.status === ActivityRewardStatus.SENT) {
          enrnedPoint += Number(slmActivityJoinedRecord.quantity);
        }
      }
    }
    return enrnedPoint;
  }
  getSLMPendingPoint(slmActivityJoinedRecords: GSLMActivityJoinedRecordWithStatus[]) {
    let pendingPoint = 0;
    if (slmActivityJoinedRecords && slmActivityJoinedRecords.length > 0) {
      for (const slmActivityJoinedRecord of slmActivityJoinedRecords) {
        if (slmActivityJoinedRecord.status === ActivityRewardStatus.SCHEDULED) {
          pendingPoint += Number(slmActivityJoinedRecord.quantity);
        }
      }
    }
    return pendingPoint;
  }
  getSLMTotalBoughtCount(slmRelationships: SLMRelationship[]) {
    let boughtCount = 0;
    if (slmRelationships && slmRelationships.length > 0) {
      boughtCount = slmRelationships.filter(relationship => relationship.status === SLMRelationshipStatus.FUNDED)
        .length;
    }
    return boughtCount;
  }
  getSLMTotalInvitedCount(slmRelationships: SLMRelationship[]) {
    let invitedCount = 0;
    if (slmRelationships && slmRelationships.length > 0) {
      for (const slmRelationship of slmRelationships) {
        if (slmRelationship.isBindMobile) {
          invitedCount += 1;
        }
      }
    }
    return invitedCount;
  }
  getAccountBalancePoint(accounts: UserAccount[]) {
    if (accounts && accounts.length > 0) {
      const account = accounts.find(account => account.accountType === AccountType.POINT);
      return account && Number(account.balance);
    }
    return 0;
  }
  getCheckInPoint(checkInActivityJoinedRecords: UserActivityJoinedRecordWithStatus[]) {
    let enrnedPoint = 0;
    if (checkInActivityJoinedRecords && checkInActivityJoinedRecords.length > 0) {
      for (const checkInActivityJoinedRecord of checkInActivityJoinedRecords) {
        if (checkInActivityJoinedRecord.status === ActivityRewardStatus.SENT) {
          enrnedPoint += Number(checkInActivityJoinedRecord.quantity);
        }
      }
    }
    return enrnedPoint;
  }
  getAccountCumulativeCompleteCourse(lessonProgresses: LessonProgresses[]) {
    const accountCumulativeCompleteCourse = lessonProgresses.filter(
      lessonProgress => lessonProgress.status === LessonStatus.PLAYED,
    ).length;
    return accountCumulativeCompleteCourse;
  }

  getAccountCumulativeClockIn(clockInActivityJoinedRecords?: ClockInActivityJoinedRecord[]) {
    return clockInActivityJoinedRecords && clockInActivityJoinedRecords.length
      ? clockInActivityJoinedRecords.length
      : 0;
  }

  changeMobile = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      mobile: e.target.value,
    });
  };
  changeNickName = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      nickName: e.target.value,
    });
  };

  onResetClick = () => {
    this.setState({
      mobile: undefined,
      nickName: undefined,
    });
  };

  getBeforeQuiz(miniGameResults: MiniGameResult[], quizEndAt: Date) {
    if (!quizEndAt) {
      return;
    }
    const effectiveQuiz = miniGameResults.filter(item => moment(item.createdAt).isSameOrBefore(moment(quizEndAt)));

    if (!effectiveQuiz.length) {
      return;
    }

    return effectiveQuiz[effectiveQuiz.length - 1];
  }

  lastContactRecord(contactRecords: UserContactRecord[]) {
    if (!contactRecords || contactRecords.length < 1) {
      return;
    }
    const [contactRecord] = contactRecords.sort((a, b) => {
      return moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf();
    });
    return contactRecord;
  }

  getLastIntent(contactRecords: UserContactRecord[]) {
    const contactRecord = this.lastContactRecord(contactRecords);
    if (!contactRecord) {
      return '';
    }
    return USER_CONTACT_RECORD_INTENT_NAME[contactRecord.intent];
  }

  getLastRemark(contactRecords: UserContactRecord[]) {
    const contactRecord = this.lastContactRecord(contactRecords);
    if (!contactRecord) {
      return '';
    }
    return contactRecord.remark;
  }

  getNumberOfCalls(contactRecords?: UserContactRecord[]) {
    const numberOfCalls =
      contactRecords && contactRecords.filter(item => item.type === UserContactRecordType.PHONE_CALL);

    return numberOfCalls && numberOfCalls.length ? numberOfCalls.length : 0;
  }

  getLastFeedback(contactRecords: UserContactRecord[]) {
    const contactRecord = this.lastContactRecord(contactRecords);
    if (!contactRecord) {
      return '';
    }
    return USER_CONTACT_RECORD_FEEDBACK_NAME[contactRecord.feedback];
  }

  onSelectDisplayColumnChange = (checked: boolean, list: ColumnDisplayList) => {
    const { columnDisplayList } = this.state;
    const newColumnDisplayList = columnDisplayList;
    switch (list) {
      case ColumnDisplayList.ADD_WECHAT:
        newColumnDisplayList.isAddWechatDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      case ColumnDisplayList.EVALUATION_LEVEL:
        newColumnDisplayList.isEvallutionLevelDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      case ColumnDisplayList.FOLLOW_UP_ACTIVITIES:
        newColumnDisplayList.isFollowUpActivitiesDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      case ColumnDisplayList.LAST_INTENT:
        newColumnDisplayList.isLastIntentDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      case ColumnDisplayList.INTENTIONALITY:
        newColumnDisplayList.isIntentionalityDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      case ColumnDisplayList.INVITATION_FOR_RETURN_FEE:
        newColumnDisplayList.isInvitationForReturnFeeDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      case ColumnDisplayList.LESSON_GIVING_ACTIVITIES:
        newColumnDisplayList.isLessonGivingActivitiesDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      case ColumnDisplayList.NUMBER_OF_CALLS:
        newColumnDisplayList.isNumberOfCallsDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      case ColumnDisplayList.RENEWAL_STATUS:
        newColumnDisplayList.isRenewalStatusDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      case ColumnDisplayList.SUPERINTENDENT:
        newColumnDisplayList.isSuperintendentDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      case ColumnDisplayList.TOTAL_CLOCK_IN:
        newColumnDisplayList.isTotalClockInDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      case ColumnDisplayList.TOTAL_LESSON_PROGRESS:
        newColumnDisplayList.isTotalLessonProgressDisplay = checked;
        this.setState({ columnDisplayList: newColumnDisplayList });
        return;
      default:
        return;
    }
  };

  getGroupsList = () => {
    let variables: {
      teacherIds?: number[];
      skuIds?: number[];
    } = { skuIds: [this.skuId], teacherIds: [this.teacherId] };
    if (this.teacherId) {
      variables = { teacherIds: [this.teacherId] };
    } else {
      variables = { skuIds: [this.skuId] };
    }

    return (
      <Query query={groupListQuery} variables={variables}>
        {({ loading, error, data }) => {
          if (error) return <div>{error.message}</div>;
          if (loading) return <div>loading...</div>;
          if (!data) {
            // @ts-ignore
            return <Empty />;
          }
          const { enrollmentGroups = [] } = data || {};
          return (
            <Select
              placeholder="请选择社群"
              showSearch
              optionFilterProp="children"
              style={{ width: '50%' }}
              onChange={(value: number) => {
                this.setState({ currentGroupId: value });
              }}
            >
              {enrollmentGroups.map((enrollmentGroup: { id: number; name: string }) => (
                <Select.Option value={enrollmentGroup.id} key={enrollmentGroup.id}>
                  {enrollmentGroup.name}
                </Select.Option>
              ))}
            </Select>
          );
        }}
      </Query>
    );
  };

  getEnrollmentList(enrollments: Enrollment[]) {
    const { mobile, nickName } = this.state;
    if (!mobile && !nickName) {
      return enrollments;
    }
    if (!mobile && nickName) {
      const enrollment = enrollments.filter(item => item.user.name === nickName);
      return enrollment;
    }
    if (!nickName && mobile) {
      const enrollment = enrollments.filter(item => item.user.mobile === mobile);
      return enrollment;
    }
    const enrollment = enrollments.filter(item => item.user.mobile === mobile && item.user.mobile === mobile);
    return enrollment;
  }

  render() {
    const {
      clockInDateRange,
      marketingActivities,
      marketingActivityFilterOptions,
      selectedMarketingActivityFilter,
      columnDisplayList,
      currentUserId,
      isAddWeChatRecord,
      isAddRecordModalDisplay,
      isCallSuccess,
      remark,
      intent,
      feedback,
      currentGroupId,
    } = this.state;
    const isCheckInDateRangeSameDay = moment(this.state.clockInDateRange.from).isSame(
      moment(this.state.clockInDateRange.to),
      'day',
    );

    if (marketingActivities.length === 0) {
      return null;
    }

    let activityId: string;
    if (marketingActivityFilterOptions.length) {
      activityId = marketingActivityFilterOptions[selectedMarketingActivityFilter].id;
    } else {
      activityId = marketingActivities[selectedMarketingActivityFilter].id;
    }

    return (
      <>
        <Query<GroupDetailData>
          query={groupDetailQuery}
          variables={{
            activityId,
            dateRange: clockInDateRange,
            miniGameName: 'admission-quiz',
            groupId: currentGroupId,
          }}
        >
          {({ loading, error, data }) => {
            if (error) return <div>{error.message}</div>;
            if (!data) {
              // @ts-ignore
              return <Empty />;
            }
            const [enrollmentGroup] = data.enrollmentGroups || [null];
            const enrollments = this.getEnrollmentList(enrollmentGroup && enrollmentGroup.enrollments);
            return (
              <>
                <Breadcrumb style={{ marginBottom: 10, fontSize: 13 }}>
                  <Breadcrumb.Item>
                    <Link to={`/Home`}>首页</Link>
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{enrollmentGroup && enrollmentGroup.name}</Breadcrumb.Item>
                </Breadcrumb>
                <Card>{this.getGroupsList()}</Card>
                <Mutation
                  mutation={updateGroupNameMutation}
                  variables={{ id: currentGroupId, name: this.state.inputGroupName }}
                >
                  {updateEnrollmentGroup => {
                    return (
                      <Modal
                        closable={false}
                        visible={this.state.isInputGroupNameModalDisplay}
                        onOk={async () => {
                          if (this.state.inputGroupName.length <= 0) {
                            this.setState({
                              isInputGroupNameMessageAlert: true,
                            });
                            return;
                          }
                          this.setState({
                            isInputGroupNameMessageAlert: false,
                            isInputGroupNameModalDisplay: false,
                            inputGroupName: '',
                          });
                          await updateEnrollmentGroup();
                        }}
                        onCancel={this.handleCancel}
                      >
                        <Input
                          maxLength={50}
                          placeholder="请输入社群名称"
                          value={this.state.inputGroupName}
                          onChange={e => {
                            this.onNameChange(e);
                          }}
                        />
                        {this.state.isInputGroupNameMessageAlert ? (
                          <Alert message="社群名称不能为空" type="error" />
                        ) : null}
                      </Modal>
                    );
                  }}
                </Mutation>
                <PageHeader
                  title={
                    <div>
                      <Row gutter={24}>
                        <Col span={5}>
                          <span>{enrollmentGroup && enrollmentGroup.name} </span>
                        </Col>
                        <Col span={17}>
                          <Button icon="edit" style={{ marginRight: 100 }} onClick={this.showModal}>
                            编辑社群名称
                          </Button>
                        </Col>
                        <Col span={2}>
                          <Button
                            type="primary"
                            icon="download"
                            onClick={() => {
                              this.exportExcel(enrollmentGroup);
                            }}
                          >
                            导出
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  }
                  content={
                    enrollmentGroup && (
                      <DescriptionList size="small" title="社群信息">
                        <Description term="管理员微信号">{enrollmentGroup.teacher.name}</Description>
                        <Description term="管理员头像">
                          <Avatar shape="square" size={64} src={enrollmentGroup.teacher.avatarUrl} />
                        </Description>
                        <Description term="管理员二维码">
                          <Avatar shape="square" size={64} src={enrollmentGroup.teacher.wechatQrCode} />
                        </Description>
                        <Description term="用户数">{enrollmentGroup.enrollments.length}</Description>
                        <Description term="SKU">{enrollmentGroup.sku.name}</Description>
                        <Description term="开课时间">
                          <FormatDate mode="date" date={enrollmentGroup.sku.start} />
                        </Description>
                        <Description term="完课时间">
                          <FormatDate mode="date" date={enrollmentGroup.sku.end} />
                        </Description>
                      </DescriptionList>
                    )
                  }
                />

                <Panel style={{ marginTop: 16 }}>
                  <Card bordered={false} title="搜索">
                    <Form>
                      <Row gutter={24}>
                        <Col xs={24} md={24} lg={8} xl={8} xxl={8}>
                          <FormItem label={`手机号码`}>
                            <Input
                              placeholder="请输入手机号"
                              allowClear
                              value={this.state.mobile}
                              onChange={this.changeMobile}
                            />
                          </FormItem>
                        </Col>
                        <Col xs={24} md={24} lg={8} xl={8} xxl={8}>
                          <FormItem label={`微信昵称`}>
                            <Input
                              placeholder="请输入微信昵称"
                              allowClear
                              value={this.state.nickName}
                              onChange={this.changeNickName}
                            />
                          </FormItem>
                        </Col>
                      </Row>
                    </Form>
                  </Card>
                  <Card bordered={false} title="展示列表">
                    <SelectDisplayColumn onSelectDisplayColumnChange={this.onSelectDisplayColumnChange} />
                  </Card>
                  <Card bordered={false} title="打卡信息">
                    <Row>
                      <Col span={10}>
                        时间范围：
                        <DatePicker.RangePicker
                          value={[moment(clockInDateRange.from), moment(clockInDateRange.to)]}
                          ranges={{
                            今天: [moment(), moment()],
                            本周: [moment().startOf('week'), moment().endOf('week')],
                            上周: [
                              moment().startOf('week').subtract(1, 'week'),
                              moment().endOf('week').subtract(1, 'week'),
                            ],
                          }}
                          onChange={date => this.onClockInDateRange(date)}
                        />
                      </Col>
                      <Col span={6}>
                        {marketingActivityFilterOptions.length > 0 &&
                          this.getBatchClockInButton((enrollmentGroup && enrollmentGroup.enrollments) || [])}
                      </Col>
                      {this.state.marketingActivityFilterOptions.length > 0 && (
                        <Col span={6}>
                          打卡活动：
                          <Select
                            style={{ width: '80%' }}
                            defaultValue={
                              this.state.marketingActivityFilterOptions[this.state.selectedMarketingActivityFilter].name
                            }
                            onChange={value => {
                              this.setState({
                                selectedMarketingActivityFilter: Number(value),
                              });
                            }}
                          >
                            {this.state.marketingActivityFilterOptions.map((option, index) => (
                              <Select.Option key={index} value={index}>
                                {option.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Col>
                      )}
                    </Row>
                  </Card>
                  <Card bordered={false} title="用户信息">
                    <MainTable enrollments={enrollments} loading={loading}>
                      <Table.Column title="用户昵称" dataIndex="user.name" key="userName" />
                      <Table.Column
                        title="宝贝名称"
                        dataIndex="user.family.children"
                        key="childName"
                        render={children => {
                          return this.getChildName(children as Child[]);
                        }}
                      />
                      <Table.Column
                        title="手机号"
                        key="userMobile"
                        render={record => {
                          const { user } = record;
                          if (!user) {
                            return;
                          }
                          return <Link to={`/user/${user.id}`}>{user.mobile}</Link>;
                        }}
                      />
                      <Table.Column title="分配时间" dataIndex="groupAllocatedAt" key="groupAllocatedAt" />
                      {/* {columnDisplayList.isSuperintendentDisplay && (
                      <Table.Column title="回访督学" dataIndex="user.mobile" key="userMobile" />
                    )} */}

                      {/* {columnDisplayList.isFollowUpActivitiesDisplay && (
                      <Table.Column title="活动跟进" dataIndex="user.mobile" key="userMobile" />
                    )} */}
                      {/* {columnDisplayList.isAddWechatDisplay && (
                      <Table.Column title="添加微信" dataIndex="user.mobile" key="userMobile" />
                    )} */}
                      {columnDisplayList.isEvallutionLevelDisplay && (
                        <Table.Column
                          title="测评等级"
                          dataIndex="miniGameResults"
                          key="miniGameResults"
                          render={miniGameResults => {
                            const beforeQuiz = this.getBeforeQuiz(miniGameResults, enrollmentGroup.sku.quizEndAt);
                            return <span>{beforeQuiz && beforeQuiz.data.level}</span>;
                          }}
                        />
                      )}

                      {/* {columnDisplayList.isInvitationForReturnFeeDisplay && (
                      <Table.Column title="邀请返学费" dataIndex="user.mobile" key="userMobile" />
                    )} */}
                      {/* {columnDisplayList.isLessonGivingActivitiesDisplay && (
                      <Table.Column title="赠课活动" dataIndex="user.mobile" key="userMobile" />
                    )} */}
                      {/* {columnDisplayList.isRenewalStatusDisplay && (
                      <Table.Column title="续费状态" dataIndex="user.mobile" key="userMobile" />
                    )} */}
                      {columnDisplayList.isIntentionalityDisplay && (
                        <Table.Column
                          title="意向度"
                          dataIndex="user.contactRecords"
                          key="intent"
                          render={contactRecords => <span>{this.getLastIntent(contactRecords)}</span>}
                        />
                      )}
                      {columnDisplayList.isLastIntentDisplay && (
                        <Table.Column
                          title="跟进记录"
                          dataIndex="user.contactRecords"
                          key="remark"
                          render={contactRecords => <span>{this.getLastRemark(contactRecords)}</span>}
                        />
                      )}

                      {columnDisplayList.isNumberOfCallsDisplay && (
                        <Table.Column
                          title="通话次数"
                          dataIndex="user.contactRecords"
                          key="numberOfCalls"
                          render={contactRecords => <span>{this.getNumberOfCalls(contactRecords)}</span>}
                        />
                      )}

                      <Table.Column
                        title="通话反馈"
                        dataIndex="user.contactRecords"
                        key="feedback"
                        render={contactRecords => <span>{this.getLastFeedback(contactRecords)}</span>}
                      />
                      {columnDisplayList.isTotalLessonProgressDisplay && (
                        <Table.Column
                          title="累计完课"
                          key="lessonProgressStatus"
                          render={record => {
                            const { id, user } = record;
                            if (!user) {
                              return;
                            }
                            return (
                              <a
                                href={`${config.BASE_URL}/#/user/${user.id}/enrollment/${id}/lesson`}
                                // eslint-disable-next-line react/jsx-no-target-blank
                                target="_blank"
                              >
                                {this.getAccountCumulativeCompleteCourse(record.lessonProgresses)}
                              </a>
                            );
                          }}
                        />
                      )}
                      {columnDisplayList.isTotalClockInDisplay && (
                        <Table.Column
                          title="累计打卡"
                          key="totalClockIn"
                          dataIndex="user.clockInActivityJoinedRecords"
                          render={clockInActivityJoinedRecords => (
                            <span>{this.getAccountCumulativeClockIn(clockInActivityJoinedRecords)}</span>
                          )}
                        />
                      )}
                      <Table.Column
                        width={'20%'}
                        align="center"
                        title="操作"
                        key="operation"
                        render={record => (
                          <>
                            {this.addRecordButton(true, record.user && record.user.id)}
                            {this.addRecordButton(false, record.user && record.user.id)}
                            {isCheckInDateRangeSameDay &&
                              this.getClockInButton(
                                record.user && record.user.clockInActivityJoinedRecords,
                                record.user && record.user.id,
                              )}
                            <Mutation mutation={RETURN_VISIT_MUTATION} variables={{ enrollmentId: record.id }}>
                              {createReturnVisit => {
                                const { returnVisit } = record;
                                const isVisited = !!(
                                  returnVisit &&
                                  returnVisit.lastReturnVisitAt &&
                                  moment().isSame(returnVisit.lastReturnVisitAt, 'isoWeek')
                                );
                                return (
                                  <CopyToClipboard
                                    text={(record.user && record.user.mobile) || ''}
                                    onCopy={async () => {
                                      message.success('复制手机号成功');
                                      !isVisited && (await createReturnVisit());
                                    }}
                                  >
                                    <Button
                                      style={{ padding: 5, marginLeft: 10, marginTop: 15 }}
                                      type={isVisited ? 'dashed' : 'primary'}
                                    >
                                      跟进
                                    </Button>
                                  </CopyToClipboard>
                                );
                              }}
                            </Mutation>
                          </>
                        )}
                      />
                      {/* <Table.Column
                        title="查看学习报告"
                        key="learningReport"
                        render={record => {
                          const { id, user } = record;
                          if (!user) {
                            return;
                          }
                          return (
                            <a
                              href={`https://crm.congmingpai.com/#/user/${user.id}/enrollment/${id}/report`}
                              // eslint-disable-next-line react/jsx-no-target-blank
                              target="_blank"
                            >
                              查看学习报告
                            </a>
                          );
                        }}
                      /> */}
                    </MainTable>
                  </Card>
                </Panel>
              </>
            );
          }}
        </Query>
        <AddRecord
          currentUserId={currentUserId}
          isAddWeChatRecord={isAddWeChatRecord}
          isAddRecordModalDisplay={isAddRecordModalDisplay}
          closeAddRecordModal={this.closeAddRecordModal}
          isCallSuccess={isCallSuccess}
          onIsCallSuccessChange={this.onIsCallSuccessChange}
          intent={intent}
          remark={remark}
          feedback={feedback}
          onIntentChange={this.onIntentChange}
          onRemarkChange={this.onRemarkChange}
          onRecordFeedbackChange={this.onRecordFeedbackChange}
          submitAddFeedback={this.submitAddFeedback}
          groupId={currentGroupId}
          activityId={activityId}
          dateRange={clockInDateRange}
        />
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/group/GroupDetail.tsx